import { list, disables, enables } from "@/apis/reimburse";
import { ReimburseCategory } from "@/types/reimburse";
import { CommonStateEnum } from "@/types/common";
import { Button, Flex, Spin, Typography, notification } from "antd";
import { FC, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReimburseTree from "./components/ReimburseTree";
import AddReimburse from "./components/AddReimburse";

type FormType = "add" | "update";

interface ReimburseCategoryIndexProps {
  activeKey: string;
}

const ReimburseCategoryIndex: FC<ReimburseCategoryIndexProps> = ({ activeKey }) => {
  const { t } = useTranslation();
  const { Title } = Typography;
  const [treeListLoading, setTreeListLoading] = useState<boolean>(false);
  const [flatData, setFlatData] = useState<Array<ReimburseCategory>>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);

  useEffect(() => {
    if (activeKey === "5") fetchList();
    else setFlatData([]);
  }, [activeKey]);

  const fetchList = () => {
    setTreeListLoading(true);
    list()
      .then((res) => setFlatData(res?.body ?? []))
      .catch((err) => {
        switch (err) {
          case 10010:
            break;
          default:
            break;
        }
      })
      .finally(() => setTreeListLoading(false));
  };

  // 将平铺数据转换为树形结构
  const buildTree = (flatList: ReimburseCategory[]): ReimburseCategory[] => {
    if (!flatList || flatList.length === 0) return [];

    const map = new Map<string, ReimburseCategory>();
    const roots: ReimburseCategory[] = [];

    // 先创建所有节点的副本
    flatList.forEach((item) => {
      map.set(item.id, { ...item, subs: null });
    });

    // 构建树形结构
    flatList.forEach((item) => {
      const node = map.get(item.id)!;
      if (item.pid && map.has(item.pid)) {
        const parent = map.get(item.pid)!;
        if (!parent.subs) {
          parent.subs = [];
        }
        parent.subs.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  // 使用 useMemo 缓存树形数据
  const treeListData = useMemo(() => buildTree(flatData), [flatData]);

  // 递归查找并更新节点（在平铺数据中）
  const updateFlatData = (list: ReimburseCategory[], id: string, newData: Partial<ReimburseCategory>): ReimburseCategory[] => {
    return list.map((item) => {
      if (item.id === id) {
        return { ...item, ...newData };
      }
      return item;
    });
  };

  // 添加新节点到平铺数据
  const addFlatData = (list: ReimburseCategory[], newNode: ReimburseCategory): ReimburseCategory[] => {
    return [...list, newNode];
  };

  const handleDataChange = (formType: FormType, result: ReimburseCategory) => {
    if (formType === "add") {
      // 添加到平铺数据
      setFlatData((prev) => addFlatData(prev, result));
      handleAddModalClose();
    } else if (formType === "update") {
      // 更新平铺数据
      setFlatData((prev) => updateFlatData(prev, result.id, result));
    }
  };

  // 收集节点及其所有子节点的ID
  const collectNodeIds = (node: ReimburseCategory, flatList: ReimburseCategory[]): string[] => {
    const ids: string[] = [node.id];
    // 查找所有子节点
    flatList.forEach((item) => {
      if (item.pid === node.id) {
        ids.push(...collectNodeIds(item, flatList));
      }
    });
    return ids;
  };

  // 禁用处理
  const handleDisable = (node: ReimburseCategory) => {
    const ids = collectNodeIds(node, flatData);
    disables(ids)
      .then(() => {
        notification.success({ placement: 'top',
          message: t("reimburse.disableSuccess"),
          description: `${node.name} ${t("reimburse.disableSuccessDesc")}`,
        });
        // 更新所有被禁用节点的state为UN_ACTIVE
        setFlatData((prev) =>
          prev.map((item) =>
            ids.includes(item.id) ? { ...item, state: CommonStateEnum.UN_ACTIVE } : item
          )
        );
      })
      .catch((err) => {
        if (err === 73405) {
          notification.error({ placement: 'top',
            message: t("reimburse.disableFailed"),
            description: t("reimburse.disableFailedHasSubs"),
          });
        } else {
          notification.error({ placement: 'top',
            message: t("reimburse.disableFailed"),
            description: t("common.pleaseTryAgain"),
          });
        }
      });
  };

  // 启用处理
  const handleEnable = (node: ReimburseCategory) => {
    const ids = collectNodeIds(node, flatData);
    enables(ids)
      .then(() => {
        notification.success({ placement: 'top',
          message: t("product.enableSuccess"),
          description: `${node.name} ${t("product.enableSuccess")}`,
        });
        // 更新所有被启用节点的state为ACTIVE
        setFlatData((prev) =>
          prev.map((item) =>
            ids.includes(item.id) ? { ...item, state: CommonStateEnum.ACTIVE } : item
          )
        );
      })
      .catch(() => {
        notification.error({ placement: 'top',
          message: t("org.enableFailed"),
          description: t("common.pleaseTryAgain"),
        });
      });
  };

  const handleAddModalClose = () => {
    setAddModalOpen(false);
  };

  return (
    <Flex vertical style={{ width: "100%" }} gap={10}>
      <Flex justify="space-between" align="baseline" style={{ marginTop: -20 }}>
        <Title level={4}>{t("layout.reimburseCategory")}</Title>
        <Button type="primary" onClick={() => setAddModalOpen(true)}>
          {t("common.add")}
        </Button>
      </Flex>
      {treeListLoading ? (
        <Spin spinning={true} style={{ marginTop: 100 }} />
      ) : (
        <ReimburseTree
          treeData={treeListData}
          onDataChange={handleDataChange}
          onEnable={handleEnable}
          onDisable={handleDisable}
        />
      )}
      <AddReimburse
        open={addModalOpen}
        formType="add"
        onSuccess={handleDataChange}
        onClose={handleAddModalClose}
      />
    </Flex>
  );
};

export default ReimburseCategoryIndex;
